import i18next from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MarkdownIt from 'markdown-it';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { useToast } from '../../../../hooks/toast';
import IActivityProps from '../../../../dtos/IActivitiesProps';
import { ResourceType } from '../../../../utils/enums';
import { Container, TextContent } from './styles';
import IStepProps from '../../../../dtos/IStepsProps';

const keywordColors: { [key: string]: string } = {
  basic: '#1e90ff',
  input: '#d400d4',
  music: '#e63022',
  led: '#5c2d91',
  radio: '#E3008C',
  loops: '#00AA00',
  logic: '#00A4A6',
  variables: '#DC153C',
  math: '#9400D3',
  functions: '#3455DB',
  arrays: '#E65722',
  text: '#B8860B',
  game: '#007A4B',
};

// Sintaxe de realce de palavra-chave: ||categoria:texto||
const keywordPattern = /\|\|(.*?):(.+?)\|\|/g;

const md = new MarkdownIt({ html: true, breaks: true });

// Linhas indentadas (4+ espaços) NÃO viram bloco de código: no material a
// indentação é apenas visual, então o negrito/listas precisam ser processados
// normalmente. Código de verdade deve usar cercas (```), que continuam funcionando.
md.disable('code');

md.use(markdown => {
  const defaultRender = markdown.renderer.rules.code_inline;

  markdown.renderer.rules.code_inline = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    let color = '';
    const text = token.content.replace(keywordPattern, (match, p1, p2) => {
      color = keywordColors[p1.trim()] || 'transparent';
      token.attrJoin('style', `background-color: ${color}; `);
      return `${p2}`;
    });
    token.content = text;
    token.markup = '';
    return defaultRender
      ? defaultRender(tokens, idx, options, env, self)
      : token.content;
  };
});

// Extracts $$...$$, $...$, and \\cmd patterns from raw markdown,
// replacing each with a numbered placeholder so they survive md.render().
const extractMathExpressions = (
  text: string,
): [string, string[], boolean[]] => {
  const exprs: string[] = [];
  const display: boolean[] = [];

  // 1. Display math: $$...$$ (may span multiple lines) — must run before inline $
  let processed = text.replace(/\$\$([\s\S]+?)\$\$/g, (_, math) => {
    exprs.push(math.trim());
    display.push(true);
    return `KMATH${exprs.length - 1}xEND`;
  });

  // 2. Inline math: $...$ (single line, no leading/trailing spaces)
  processed = processed.replace(/\$([^$\n]+?)\$/g, (_, math) => {
    exprs.push(math.trim());
    display.push(false);
    return `KMATH${exprs.length - 1}xEND`;
  });

  // 3. Legacy \\cmd sequences from the double-backslash convention in .md files
  processed = processed.replace(
    /([a-zA-Z]*)((?:\\\\[a-zA-Z]+(?:\{[^{}]*\})?[a-zA-Z0-9^_.{},\/ ]*)+)/g,
    (_, prefix, latex) => {
      const math = (prefix + latex.replace(/\\\\/g, '\\')).trimEnd();
      exprs.push(math);
      display.push(false);
      return `KMATH${exprs.length - 1}xEND`;
    },
  );

  return [processed, exprs, display];
};

// Replaces KMATH placeholders in rendered HTML with KaTeX output.
const injectKatex = (
  html: string,
  exprs: string[],
  display: boolean[],
): string => {
  return html.replace(/KMATH(\d+)xEND/g, (_, i) => {
    try {
      return katex.renderToString(exprs[+i], {
        throwOnError: false,
        output: 'html',
        displayMode: display[+i],
      });
    } catch {
      return exprs[+i];
    }
  });
};

// Aplica `transform` apenas ao texto fora de <pre>...</pre> e <code>...</code>,
// preservando o conteúdo de blocos e trechos de código.
const transformOutsideCode = (
  html: string,
  transform: (text: string) => string,
): string =>
  html
    .split(/(<pre[\s\S]*?<\/pre>|<code[\s\S]*?<\/code>)/g)
    .map((segment, i) => (i % 2 === 0 ? transform(segment) : segment))
    .join('');

// Converte qualquer **negrito** restante que o markdown-it (CommonMark) recusou,
// inclusive grudado a letras/pontuação — comportamento estilo Notion. Só negrito.
const boldAll = (text: string): string =>
  text.replace(
    /\*\*(\S(?:[^\n]*?\S)?)\*\*/g,
    (_, content) => `<strong>${content}</strong>`,
  );

const fixBoldAdjacent = (html: string): string =>
  transformOutsideCode(html, boldAll);

// Realça ||categoria:texto|| escrito em texto puro (fora de crases), igual ao que
// o renderer de code_inline já faz dentro de crases. Categoria desconhecida vira
// apenas o texto, sem realce.
const injectKeywordColors = (html: string): string =>
  transformOutsideCode(html, text =>
    text.replace(keywordPattern, (_, cat, content) => {
      const color = keywordColors[cat.trim()];
      return color
        ? `<code style="background-color: ${color};">${content}</code>`
        : content;
    }),
  );

interface IComponentProps {
  currentActivity: IActivityProps;
  currentStep: IStepProps;
}

const MarkdownComponent: React.FC<IComponentProps> = ({
  currentStep: step,
  currentActivity,
}) => {
  const [descriptionText, setDescriptionText] = useState('');

  const { addToast } = useToast();
  const { t } = useTranslation();

  const loadDescriptionMarkdown = useCallback(
    async (currentStep: IStepProps) => {
      let descriptionURL = '';

      if (currentStep && step.resourse_type === ResourceType.MARKDOWN) {
        if (i18next.language === 'en') {
          descriptionURL = step.resource_url_en;
        }

        if (i18next.language === 'pt-BR') {
          descriptionURL = step.resource_url;
        }
      }

      if (!descriptionURL) {
        // Não prossiga se a URL não foi definida
        return;
      }

      try {
        const response = await fetch(descriptionURL);

        const text = await response.text();

        const [mathText, mathExprs, mathDisplay] = extractMathExpressions(text);
        const html = injectKatex(
          fixBoldAdjacent(injectKeywordColors(md.render(mathText))),
          mathExprs,
          mathDisplay,
        );

        setDescriptionText(html);
      } catch (error) {
        addToast({
          type: 'error',
          expression: 'Ops!',
          description: t('toasts.errors.load_description_fail'),
        });
      }
    },
    [addToast, step, t],
  );

  useEffect(() => {
    if (step) {
      loadDescriptionMarkdown(step);
    }
  }, [loadDescriptionMarkdown, step]);

  return (
    <Container>
      <TextContent>
        {currentActivity && (
          <div>
            <div
              className="markdown-container"
              dangerouslySetInnerHTML={{ __html: descriptionText }}
            />
          </div>
        )}
      </TextContent>
    </Container>
  );
};

export default MarkdownComponent;
